import { createContext, useContext, useState, useEffect, useMemo, useRef } from "react";
import { translations } from "@/i18n/translations";
import { useAuth } from "@/context/AuthContext";

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const { user } = useAuth();
  const [lang, setLang] = useState(localStorage.getItem("quotesys_lang") || "zh");
  const appliedForUserId = useRef(null);

  // When the auth-loaded user's saved language differs from what we're using,
  // adopt it (once per user session — user can still override with toggle).
  useEffect(() => {
    if (user && user.id && appliedForUserId.current !== user.id) {
      const preferred = user.language === "en" || user.language === "zh" ? user.language : null;
      if (preferred && preferred !== lang) setLang(preferred);
      appliedForUserId.current = user.id;
    } else if (!user) {
      appliedForUserId.current = null;
    }
  }, [user, lang]);

  useEffect(() => {
    localStorage.setItem("quotesys_lang", lang);
    document.documentElement.lang = lang === "zh" ? "zh-CN" : "en";
  }, [lang]);

  const value = useMemo(() => ({
    lang,
    t: (key) => translations[lang]?.[key] || translations.en[key] || key,
    toggleLang: () => setLang((l) => (l === "en" ? "zh" : "en")),
    setLang,
  }), [lang]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
